import React from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Divider,
  IconButton,
  Button,
} from "@mui/material";
import CustomFieldDisplay from "./CustomFieldDisplay";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from '@mui/icons-material/Edit';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { theme } from "../../../../../theme";
import CustomFieldRowBase from "./CustomFieldRowBase";
import { CustomField, SectionProps } from "./CandidateDetailsConfiguration";
import DeleteIcon from "@mui/icons-material/Delete";

interface SkillsSectionProps {
  retrievedFields: CustomField[];
  skillsCustomFields: CustomField[];
  skillsFieldErrors: { name?: string; type?: string }[];
  handleEditCustomField: (customFieldId: number) => void;
  setDeleteDialogOpen: (open: boolean) => void;
  setCustomFieldIdToDelete: (id: number | null) => void;
  handleAddCustomField: (
    section: string,
    setFields: React.Dispatch<React.SetStateAction<CustomField[]>>
  ) => void;
  setSkillsCustomFields: React.Dispatch<React.SetStateAction<CustomField[]>>;
  handleCustomFieldChange: (
    index: number,
    updatedField: CustomField,
    setFields: React.Dispatch<React.SetStateAction<CustomField[]>>,
    setErrors: React.Dispatch<React.SetStateAction<Record<string, any>[]>>
  ) => void;
  handleDeleteCustomField: (
    index: number,
    setFields: React.Dispatch<React.SetStateAction<CustomField[]>>,
    setErrors: React.Dispatch<React.SetStateAction<Record<string, any>[]>>
  ) => void;
  handleSaveCustomField: (
    index: number,
    fieldArray: CustomField[],
    setFieldArray: React.Dispatch<React.SetStateAction<CustomField[]>>,
    fieldErrorsSetter: React.Dispatch<React.SetStateAction<any[]>>,
    sectionId: number
  ) => void;
  setSkillsFieldErrors: React.Dispatch<React.SetStateAction<{ name?: string; type?: string }[]>>;
  setSection: (section: string) => void;
  sectionData: SectionProps;
}

const SkillsSection: React.FC<SkillsSectionProps> = ({
  retrievedFields,
  skillsCustomFields,
  skillsFieldErrors,
  handleEditCustomField,
  setDeleteDialogOpen,
  setCustomFieldIdToDelete,
  handleAddCustomField,
  setSkillsCustomFields,
  handleCustomFieldChange,
  handleDeleteCustomField,
  handleSaveCustomField,
  setSkillsFieldErrors,
  setSection,
  sectionData
}) => (
  <>
    <Typography variant="subtitle2" color="#101828" mb={1}>
      {sectionData?.sectionName}
    </Typography>

    <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            {retrievedFields
              .filter((field) => field.sectionName === sectionData.sectionName)
              .reduce((acc: any[][], curr, i) => {
                if (i % 1 === 0) acc.push([curr]);
                else acc[acc.length - 1].push(curr);
                return acc;
              }, [])
              .map((fieldPair, rowIndex) => (
                <Grid container item spacing={2} key={rowIndex}>
                  {fieldPair.map((field, colIndex) => (
                    <Grid item xs={12} key={colIndex}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CustomFieldDisplay field={field} />
                        {/* {field.isDefault ? (<></>) : ( */}
                          <>
                            <IconButton
                              color="primary"
                              onClick={() => handleEditCustomField(field.customFieldId)}
                              title="Edit Custom Field"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              onClick={() => {
                                setDeleteDialogOpen(true);
                                setCustomFieldIdToDelete(field.customFieldId);
                              }}
                              title="Delete Custom Field"
                            >
                              <DeleteIcon fontSize="medium" color="primary" />
                            </IconButton>
                          </>
                        {/* )} */}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ))}
          </Grid>
        </Grid>
        <Grid item xs={12} display="flex" alignItems="center">
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleAddCustomField(sectionData.sectionName, setSkillsCustomFields)}
            sx={{
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
              ml: "auto"
            }}
          >
            Add Custom Field
          </Button>
        </Grid>
      </Grid>
      {skillsCustomFields.map((field, index) => (
        <CustomFieldRowBase
          key={index}
          field={field}
          error={skillsFieldErrors[index] || {}}
          onChange={(updatedField) => handleCustomFieldChange(index, updatedField, setSkillsCustomFields, setSkillsFieldErrors)}
          onDelete={() => {
            if (field.customFieldId !== 0) {
              setDeleteDialogOpen(true);
              setCustomFieldIdToDelete(field.customFieldId);
              setSection(sectionData.sectionName);
            } else {
              handleDeleteCustomField(index, setSkillsCustomFields, setSkillsFieldErrors);
            }
          }}
          onSave={() => handleSaveCustomField(index, skillsCustomFields, setSkillsCustomFields, setSkillsFieldErrors, sectionData.sectionId)}
          isNew={true}
        />
      ))}
    </Paper>
  </>
);

export default SkillsSection;